/**
 * Styled components for SideNav: wrapper, items container, toggle handle,
 * expanded panel, resize handle, and panel control button.
 */
import styled from "styled-components";
import { css } from "@styled-system/css";
import { themeGet } from "@styled-system/theme-get";
import Popover from "../../Popover";
import {
  BREAKPOINTS,
  EXPANDED_SIZE,
  SIDENAV_WIDTHS,
} from "../constants/sideNav";

const createShouldForwardProp = (blockedProps) =>
  function shouldForwardProp(prop, defaultValidatorFn) {
    if (blockedProps.includes(prop)) {
      return false;
    }

    if (typeof defaultValidatorFn === "function") {
      return defaultValidatorFn(prop);
    }

    return true;
  };

const filterIsExpandedProp = createShouldForwardProp(["isExpanded"]);

export const SideNavWrapper = styled("div").withConfig({
  shouldForwardProp: createShouldForwardProp(["sideNavHeight"]),
})((props) =>
  css({
    bg: themeGet("colors.white")(props),
    color: themeGet("colors.greyDarkest")(props),
    minHeight: props.sideNavHeight || "100vh",
    height: "100%",
    maxWidth: "max-content",
    fontFamily: themeGet("fonts.main")(props),
    borderRight: `solid 1px ${themeGet("colors.greyLighter")(props)}`,
    display: "flex",
    alignItems: "stretch",
    position: "relative",
    alignContent: "stretch",
    zIndex: "5",
    flexShrink: "0",
    alignSelf: "stretch",
    [`@media screen and (max-width: ${BREAKPOINTS.SMALL_SCREEN}px)`]: {
      borderRight: "none",
      borderTop: `solid 1px ${themeGet("colors.greyLighter")(props)}`,
      width: "100%",
      height: "auto",
      minWidth: "auto",
      maxWidth: "initial",
      minHeight: "initial",
      maxHeight: themeGet("appScale.sidebarMobileHeight", "100vh")(props),
      position: "fixed",
      bottom: "0",
      left: "0",
      zIndex: "1000",
      flexDirection: "column-reverse",
    },
  })
);

export const SideNavItems = styled("div").withConfig({
  shouldForwardProp: filterIsExpandedProp,
})((props) =>
  css({
    width: props.isExpanded
      ? SIDENAV_WIDTHS.EXPANDED
      : themeGet("appScale.sideNavSize")(props),
    height: "100%",
    overflowX: "hidden",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    flexShrink: "0",
    flexGrow: "1",
    padding: "10px 10px 10px 10px",
    gap: "4px",
    alignItems: "center",
    justifyContent: "flex-start",
    textAlign: "center",
    position: "relative",
    minHeight: "100%",

    // Expanded state overrides
    ...(props.isExpanded && {
      padding: "10px 9px 10px 9px",
      alignItems: "stretch",
      textAlign: "left",
    }),

    [`@media screen and (max-width: ${BREAKPOINTS.SMALL_SCREEN}px)`]: {
      padding: "10px",
      height: "auto",
      flexDirection: "row",
      justifyContent: "space-around",
      alignItems: "center",
      width: "auto",
      zIndex: 0,
      position: "relative",
    },
  })
);

export const PanelControlTooltip = styled(Popover)`
  ${(props) =>
    props.position === "absolute" &&
    css({
      position: "absolute",
      right: themeGet("space.r")(props),
      top: "8px",
    })}
`;

export const PanelControl = styled("button")((props) =>
  css({
    borderRadius: "50%",
    width: "24px",
    height: "24px",
    background: themeGet("colors.greyLighter")(props),
    color: themeGet("colors.greyDarker")(props),
    fontSize: "1.2rem",
    appearance: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    border: "none",
    transition: themeGet("transition.transitionDefault")(props),
    "&:hover, &:focus": {
      outline: "none",
      backgroundColor: themeGet("colors.primaryLightest")(props),
    },
    "&:focus-visible": {
      outline: `2px solid ${themeGet("colors.primary")(props)}`,
      outlineOffset: "2px",
    },
  })
);

export const ResizeHandle = styled("div")((props) =>
  css({
    position: "absolute",
    right: "-8px",
    top: "0",
    bottom: "0",
    width: "13px",
    padding: "0 5px",
    backgroundClip: "content-box",
    height: "100%",
    zIndex: "13",
    cursor: "col-resize",
    backgroundColor: "transparent",
    transition: themeGet("transition.transitionDefault")(props),
    "&:hover": {
      backgroundColor: themeGet("colors.primaryLighter")(props),
      ".resize-icon": {
        backgroundColor: themeGet("colors.primaryLighter")(props),
        opacity: "1",
      },
    },
    "&:active": {
      backgroundColor: themeGet("colors.primaryLight")(props),
      ".resize-icon": {
        backgroundColor: themeGet("colors.primaryLight")(props),
      },
    },
    [`@media screen and (max-width: ${BREAKPOINTS.SMALL_SCREEN}px)`]: {
      right: "0",
      top: "-8px",
      left: "0",
      padding: "5px 0",
      bottom: "auto",
      width: "100%",
      height: "13px",
      cursor: "row-resize",
    },
  })
);

export const ToggleHandle = styled("button").withConfig({
  shouldForwardProp: filterIsExpandedProp,
})((props) =>
  css({
    position: "absolute",
    left: props.isExpanded
      ? `calc(${SIDENAV_WIDTHS.EXPANDED} - 8px)`
      : `calc(${themeGet("appScale.sideNavSize")(props)} - 8px)`,
    top: "0",
    bottom: "0",
    width: "16px",
    padding: "0 5px",
    backgroundClip: "content-box",
    height: "100%",
    zIndex: "100",
    cursor: "col-resize",
    backgroundColor: "transparent",
    transition: "none",
    appearance: "none",
    fontFamily: themeGet("fonts.main")(props),
    border: "none",
    outline: "none",
    "&:hover": {
      backgroundColor: themeGet("colors.primaryLighter")(props),
      ".toggle-icon": {
        backgroundColor: themeGet("colors.primaryLighter")(props),
        opacity: "1",
      },
    },
    "&:active": {
      backgroundColor: themeGet("colors.primaryLight")(props),
      ".toggle-icon": {
        backgroundColor: themeGet("colors.primaryLight")(props),
      },
    },
  })
);

export const ToggleIcon = styled("div")((props) =>
  css({
    position: "absolute",
    opacity: "0",
    top: "20px",
    left: "50%",
    height: "28px",
    width: "28px",
    backgroundColor: themeGet("colors.white")(props),
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: themeGet("colors.white")(props),
    fontSize: "12px",
    fontWeight: "bold",
    transform: "translateX(-50%)",
    transition: themeGet("transition.transitionDefault")(props),
    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
    pointerEvents: "none",
    svg: {
      color: themeGet("colors.white")(props),
    },
    "&.resize-icon": {
      width: "28px",
      height: "28px",
      top: "20px",
      left: "50%",
      right: "auto",
      borderRadius: "4px",
      transform: "translateX(-50%)",
      boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
    },
    [`@media screen and (max-width: ${BREAKPOINTS.SMALL_SCREEN}px)`]: {
      "&.resize-icon": {
        left: "50%",
        top: "6px",
        flexDirection: "column",
        transform: "translate(-50%, -50%)",
        width: "93px",
        borderRadius: "0 0 2px 2px",
        height: "15px",
        "& > span": {
          height: "6px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      },
    },
  })
);

export const SideNavExpanded = styled("div")((props) =>
  css({
    position: "relative",
    backgroundColor: themeGet("colors.white")(props),
    display: props.active ? "block" : "none",
    width: props.width ? `${props.width}px` : "auto",
    minWidth: props.large
      ? `${EXPANDED_SIZE.largeMin}px`
      : `${EXPANDED_SIZE.normalMin}px`,
    maxWidth: props.large
      ? `${EXPANDED_SIZE.largeMax}px`
      : `${EXPANDED_SIZE.normalMax}px`,
    height: "inherit", // Always inherit height on desktop
    overflowX: "hidden",
    overflowY: "auto",
    padding: "10px 16px 16px 16px",
    borderLeft: `solid 1px ${themeGet("colors.greyLighter")(props)}`,
    zIndex: 1,
    "&:focus": {
      outline: "0",
    },
    [`@media screen and (max-width: ${BREAKPOINTS.SMALL_SCREEN}px)`]: {
      width: "100vw",
      minWidth: "initial",
      maxWidth: "initial",
      borderLeft: "none",
      borderTop: `solid 1px ${themeGet("colors.greyLighter")(props)}`,
      borderBottom: `solid 1px ${themeGet("colors.greyLighter")(props)}`,
      height: props.height ? `${props.height}px` : "auto", // Only apply height on mobile
      minHeight: props.large
        ? `${EXPANDED_SIZE.largeMin}px`
        : `${EXPANDED_SIZE.normalMin}px`,
      maxHeight: props.large
        ? `${EXPANDED_SIZE.largeMax}px`
        : `${EXPANDED_SIZE.normalMax}px`,
      zIndex: 1001,
      position: "absolute",
      bottom: "100%",
      left: "0",
      right: "0",
    },
  })
);
